import styled from "styled-components";
import { Link } from "react-router-dom";
import { media } from "../../styles/media";

export const NavRoot = styled.nav`
  position: sticky;
  top: 0;
  z-index: 50;
  background: hsl(${({ theme }) => theme.colors.navDark});
`;

export const NavInner = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  column-gap: 0.75rem;
  height: 4rem;
  max-width: ${({ theme }) => theme.layout.shellMax};
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.layout.shellPadX};
  ${media.md} {
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
    column-gap: clamp(0.75rem, 2vw, 1.5rem);
  }
`;

/** Column 1: logo only (far left). */
export const NavBrandSlot = styled.div`
  justify-self: start;
  min-width: 0;
`;

/** Column 2: primary nav links, centered (hidden on small screens). */
export const NavCenterSlot = styled.div`
  display: none;
  justify-self: center;
  min-width: 0;
  ${media.md} {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

export const Brand = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 800;
  font-size: clamp(1.125rem, 1rem + 0.5vw, 1.375rem);
  flex-shrink: 0;
`;

export const BrandAccent = styled.span`
  background: hsl(${({ theme }) => theme.colors.accent});
  color: hsl(${({ theme }) => theme.colors.accentForeground});
  padding: 0.125rem 0.5rem;
  border-radius: 0.25rem;
`;

export const BrandRest = styled.span`
  color: hsl(${({ theme }) => theme.colors.navDarkForeground});
`;

export const DesktopLinks = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: clamp(1rem, 1.8vw, 2rem);
  min-width: 0;
`;

export const NavLinkStyled = styled(Link)`
  color: hsl(${({ theme }) => theme.colors.navDarkForeground} / 0.7);
  font-weight: 500;
  font-size: clamp(0.875rem, 0.82rem + 0.2vw, 1rem);
  transition: color 0.15s;
  &:hover {
    color: hsl(${({ theme }) => theme.colors.navDarkForeground});
  }
`;

export const NavSearchBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  flex-shrink: 0;
  border-radius: ${({ theme }) => theme.radius.lg};
  color: hsl(${({ theme }) => theme.colors.navDarkForeground} / 0.85);
  &:hover {
    background: hsl(${({ theme }) => theme.colors.navDarkForeground} / 0.12);
    color: hsl(${({ theme }) => theme.colors.navDarkForeground});
  }
`;

export const RightCluster = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 1rem;
  flex-shrink: 0;
  justify-self: end;
  min-width: 0;
`;

export const AccentLink = styled(Link)`
  display: none;
  color: hsl(${({ theme }) => theme.colors.accent});
  font-weight: 500;
  font-size: clamp(0.875rem, 0.82rem + 0.2vw, 1rem);
  transition: opacity 0.15s;
  &:hover {
    opacity: 0.85;
  }
  ${media.lg} {
    display: inline;
  }
`;

export const GhostLink = styled(Link)`
  display: none;
  color: hsl(${({ theme }) => theme.colors.navDarkForeground} / 0.7);
  font-weight: 500;
  font-size: clamp(0.875rem, 0.82rem + 0.2vw, 1rem);
  &:hover {
    color: hsl(${({ theme }) => theme.colors.navDarkForeground});
  }
  ${media.md} {
    display: inline;
  }
`;

/** Same look as GhostLink, for sign-out (button, not anchor). */
export const GhostButton = styled.button`
  display: none;
  border: none;
  background: none;
  cursor: pointer;
  padding: 0;
  font: inherit;
  color: hsl(${({ theme }) => theme.colors.navDarkForeground} / 0.7);
  font-weight: 500;
  font-size: clamp(0.875rem, 0.82rem + 0.2vw, 1rem);
  &:hover {
    color: hsl(${({ theme }) => theme.colors.navDarkForeground});
  }
  ${media.md} {
    display: inline;
  }
`;

export const JoinBtn = styled(Link)`
  background: hsl(${({ theme }) => theme.colors.accent});
  color: hsl(${({ theme }) => theme.colors.accentForeground});
  padding: 0.5rem 1.5rem;
  border-radius: ${({ theme }) => theme.radius.md};
  font-weight: 600;
  font-size: clamp(0.875rem, 0.82rem + 0.2vw, 1rem);
  transition: opacity 0.15s;
  &:hover {
    opacity: 0.9;
  }
`;

export const IconBtn = styled.button`
  display: flex;
  color: hsl(${({ theme }) => theme.colors.navDarkForeground});
  ${media.md} {
    display: none;
  }
`;

export const MobilePanel = styled.div`
  display: block;
  border-top: 1px solid hsl(${({ theme }) => theme.colors.navDarkForeground} / 0.1);
  padding: 1rem;
  ${media.md} {
    display: none;
  }
`;

export const MobileLink = styled(Link)`
  display: block;
  padding: 0.25rem 0;
  font-size: clamp(0.875rem, 0.82rem + 0.2vw, 1rem);
  font-weight: 500;
  color: hsl(${({ theme }) => theme.colors.navDarkForeground} / 0.85);
  &:hover {
    color: hsl(${({ theme }) => theme.colors.navDarkForeground});
  }
`;

export const MobileAccent = styled(Link)`
  display: block;
  font-size: clamp(0.875rem, 0.82rem + 0.2vw, 1rem);
  font-weight: 500;
  color: hsl(${({ theme }) => theme.colors.accent});
`;

export const MobileDivider = styled.div`
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid hsl(${({ theme }) => theme.colors.navDarkForeground} / 0.1);
`;

/** Block-level control in the mobile drawer (same rhythm as MobileLink). */
export const MobilePlainButton = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  border: none;
  background: none;
  cursor: pointer;
  padding: 0.25rem 0;
  font-size: clamp(0.875rem, 0.82rem + 0.2vw, 1rem);
  font-weight: 500;
  color: hsl(${({ theme }) => theme.colors.navDarkForeground} / 0.85);
  &:hover {
    color: hsl(${({ theme }) => theme.colors.navDarkForeground});
  }
`;

export const NavUserLabel = styled.span`
  display: none;
  max-width: 10rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: clamp(0.8125rem, 0.78rem + 0.15vw, 0.9375rem);
  font-weight: 500;
  color: hsl(${({ theme }) => theme.colors.navDarkForeground} / 0.65);
  ${media.md} {
    display: inline;
  }
`;

export const MobileSearchTrigger = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  margin-top: 0.75rem;
  padding: 0.625rem 0.75rem;
  border-radius: ${({ theme }) => theme.radius.lg};
  font-size: clamp(0.875rem, 0.82rem + 0.2vw, 1rem);
  font-weight: 500;
  text-align: left;
  color: hsl(${({ theme }) => theme.colors.navDarkForeground} / 0.9);
  background: hsl(${({ theme }) => theme.colors.navDarkForeground} / 0.08);
  &:hover {
    background: hsl(${({ theme }) => theme.colors.navDarkForeground} / 0.14);
  }
`;
